import React, { useRef, useState, useEffect } from 'react';
import { Upload, X, RotateCcw, ZoomIn, ZoomOut, AlertCircle, Loader } from 'lucide-react';

/**
 * SignatureCapture Component
 *
 * Captures signatures via three methods: Draw (canvas), Upload (image file), E-Sign (placeholder).
 * Replaces the original SignatureModal with enhanced features.
 *
 * Props:
 * - onSignatureCapture: function(signature_data) - Called when signature is captured
 *   signature_data = { method, image: base64 | url, name }
 * - initialName: string - Pre-filled name
 * - agreementId: string - For E-Sign integration
 * - onClose: function - Called when modal closes
 */
const SignatureCapture = ({
  onSignatureCapture = null,
  initialName = '',
  agreementId = null,
  onClose = null,
}) => {
  // Tabs
  const [activeTab, setActiveTab] = useState('draw'); // 'draw' | 'upload' | 'esign'

  // Draw tab state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(3);
  const [drawCanvas, setDrawCanvas] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Upload tab state
  const fileInputRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Common state
  const [name, setName] = useState(initialName);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [errors, setErrors] = useState({});

  // E-Sign state
  const [eSignPhone, setESignPhone] = useState('');
  const [eSignLoading, setESignLoading] = useState(false);

  // Initialize canvas
  useEffect(() => {
    // Reinitialize canvas whenever draw tab becomes active
    if (activeTab === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext('2d');
      // Set white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Set drawing style
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setDrawCanvas(ctx);
    }
  }, [activeTab, brushSize]); // Remove drawCanvas from deps to avoid stale references

  // ============ DRAW TAB HANDLERS ============

  const startDrawing = (e) => {
    if (!drawCanvas || activeTab !== 'draw') return;
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    drawCanvas.beginPath();
    drawCanvas.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !drawCanvas || activeTab !== 'draw') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    drawCanvas.lineWidth = brushSize;
    drawCanvas.lineTo(x, y);
    drawCanvas.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (!drawCanvas) return;
    drawCanvas.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!drawCanvas || !canvasRef.current) return;
    drawCanvas.fillStyle = '#ffffff';
    drawCanvas.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawn(false);
    setSelectedSignature(null);
  };

  const captureDrawnSignature = () => {
    if (!canvasRef.current || !hasDrawn) {
      setErrors({ draw: 'Please draw a signature' });
      return;
    }

    if (!name.trim()) {
      setErrors({ name: 'Please enter your name' });
      return;
    }

    const base64 = canvasRef.current.toDataURL('image/png');
    setSelectedSignature({
      method: 'draw',
      image: base64,
      name: name,
      canvas: canvasRef.current.toDataURL('image/png'),
    });
    setErrors({});
  };

  // ============ UPLOAD TAB HANDLERS ============

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setUploadProgress(0);

    // Validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only PNG, JPG, and GIF files are allowed');
      return;
    }

    if (file.size > maxSize) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    // Read file
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        setUploadProgress(percentComplete);
      }
    };

    reader.onload = (event) => {
      const base64String = event.target.result;
      setUploadedImage(base64String);
      setUploadedFileName(file.name);
      setUploadProgress(100);

      // Auto-select if valid
      if (name.trim()) {
        setSelectedSignature({
          method: 'upload',
          image: base64String,
          name: name,
          fileName: file.name,
        });
      }
    };

    reader.onerror = () => {
      setUploadError('Failed to read file');
    };

    reader.readAsDataURL(file);
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setUploadedFileName('');
    setUploadProgress(0);
    setSelectedSignature(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ============ E-SIGN TAB HANDLERS ============

  const initiateESign = async () => {
    if (!eSignPhone.trim()) {
      setErrors({ eSignPhone: 'Please enter your phone number' });
      return;
    }

    if (!name.trim()) {
      setErrors({ name: 'Please enter your name' });
      return;
    }

    setESignLoading(true);
    setErrors({});

    try {
      // Placeholder: E-Sign request will be handled in the signing flow
      // This will be integrated with Surepass NSDL later
      setSelectedSignature({
        method: 'esign',
        phone: eSignPhone,
        name: name,
        placeholder: true,
      });
    } catch (err) {
      setErrors({
        esign: err.message || 'Failed to initiate E-Sign',
      });
    } finally {
      setESignLoading(false);
    }
  };

  // ============ FINAL SUBMISSION ============

  // Auto-capture when signature is ready (no separate confirmation button needed)
  useEffect(() => {
    if (selectedSignature && name.trim() && onSignatureCapture) {
      onSignatureCapture({
        ...selectedSignature,
        name: name,
      });
    }
  }, [selectedSignature, name, onSignatureCapture]);

  // ============ RENDER ============

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => {
            setActiveTab('draw');
            setErrors({});
          }}
          className={`flex-1 py-3 px-4 font-medium text-sm transition ${
            activeTab === 'draw'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Draw Signature
        </button>
        <button
          onClick={() => {
            setActiveTab('upload');
            setErrors({});
          }}
          className={`flex-1 py-3 px-4 font-medium text-sm transition ${
            activeTab === 'upload'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Upload Image
        </button>
        <button
          onClick={() => {
            setActiveTab('esign');
            setErrors({});
          }}
          className={`flex-1 py-3 px-4 font-medium text-sm transition ${
            activeTab === 'esign'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          E-Sign
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* DRAW TAB */}
        {activeTab === 'draw' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Draw Your Signature
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Use your mouse or trackpad to draw your signature in the box below
              </p>

              {/* Canvas */}
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  width="500"
                  height="200"
                  className="w-full cursor-crosshair bg-white block"
                />
              </div>

              {/* Brush Size Control */}
              <div className="mt-3 flex items-center gap-3">
                <label className="text-sm text-gray-600">Brush Size:</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-32"
                />
                <span className="text-sm text-gray-600">{brushSize}px</span>
              </div>

              {/* Clear Button */}
              <div className="mt-3 flex gap-2">
                <button
                  onClick={clearCanvas}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition"
                >
                  <RotateCcw className="w-4 h-4" />
                  Clear
                </button>
                {hasDrawn && (
                  <button
                    onClick={captureDrawnSignature}
                    className="ml-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition"
                  >
                    Use This Signature
                  </button>
                )}
              </div>

              {errors.draw && (
                <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {errors.draw}
                </div>
              )}
            </div>
          </div>
        )}

        {/* UPLOAD TAB */}
        {activeTab === 'upload' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Upload Signature Image
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Supports PNG, JPG, and GIF files up to 5MB
              </p>

              {!uploadedImage ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition"
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="font-medium text-gray-900">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, or GIF (max 5MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Image Preview */}
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <img
                      src={uploadedImage}
                      alt="Uploaded signature"
                      className="max-w-full max-h-48 mx-auto"
                    />
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {uploadedFileName}
                    </p>
                  </div>

                  {/* Replace/Remove */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition"
                    >
                      Choose Different File
                    </button>
                    <button
                      onClick={removeUploadedImage}
                      className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Auto-select if name provided */}
                  {selectedSignature?.method === 'upload' && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <span>✓</span>
                      <span>Image selected</span>
                    </div>
                  )}
                </div>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{Math.round(uploadProgress)}%</p>
                </div>
              )}

              {uploadError && (
                <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {uploadError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* E-SIGN TAB */}
        {activeTab === 'esign' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Surepass E-Sign (Powered by NSDL)</h4>
              <p className="text-sm text-blue-800 mb-3">
                E-Sign provides a legally recognized digital signature through NSDL's secure platform.
              </p>
              <p className="text-xs text-blue-700">
                Coming soon! We're integrating Surepass NSDL e-signature capabilities.
                Meanwhile, use Draw or Upload methods to sign your agreement.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Your Phone Number
              </label>
              <input
                type="tel"
                placeholder="+91 98765 43210"
                value={eSignPhone}
                onChange={(e) => setESignPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.eSignPhone && (
                <p className="text-xs text-red-600 mt-1">{errors.eSignPhone}</p>
              )}
            </div>

            <button
              onClick={initiateESign}
              disabled={eSignLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed"
            >
              {eSignLoading && <Loader className="w-4 h-4 animate-spin" />}
              <span>Coming Soon</span>
            </button>

            {errors.esign && (
              <div className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.esign}
              </div>
            )}
          </div>
        )}

        {/* Name Field (Common) */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Your Full Name
          </label>
          <input
            type="text"
            placeholder="Enter your full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.name && (
            <p className="text-xs text-red-600 mt-1">{errors.name}</p>
          )}
        </div>

        {/* Selected Signature Preview */}
        {selectedSignature && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Signature Preview
            </h4>
            <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex items-center justify-center">
              {selectedSignature.method === 'draw' && (
                <img
                  src={selectedSignature.image}
                  alt="Drawn signature"
                  className="max-w-full max-h-32"
                />
              )}
              {selectedSignature.method === 'upload' && (
                <img
                  src={selectedSignature.image}
                  alt="Uploaded signature"
                  className="max-w-full max-h-32"
                />
              )}
              {selectedSignature.method === 'esign' && (
                <div className="text-center">
                  <p className="text-sm text-gray-600">E-Sign via Surepass NSDL</p>
                  <p className="text-xs text-gray-500 mt-1">{selectedSignature.phone}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status Indicator (auto-capture when ready) */}
        {selectedSignature && name.trim() && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <span className="text-green-600 text-xl">✓</span>
            <div>
              <p className="font-semibold text-green-900">Signature captured</p>
              <p className="text-xs text-green-700">Scroll down to continue</p>
            </div>
          </div>
        )}

        {/* Back Button */}
        {onClose && (
          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
            >
              ← Back
            </button>
          </div>
        )}

        {/* General Errors */}
        {errors.general && (
          <div className="mt-4 text-sm text-red-600 flex items-center gap-2 bg-red-50 p-3 rounded">
            <AlertCircle className="w-4 h-4" />
            {errors.general}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureCapture;
